import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { catchError, Observable, of, take, tap } from 'rxjs';

import { ProfileService } from '@tfs/auth';

import { AccountResetProgressModalComponent } from '../feature-account-reset-progress/account-reset-progress-modal.component';
import { isResetRunning } from '../models/account-reset.models';

import { AccountResetApi } from './account-reset.api';

/**
 * The account owner's reset of their own account. While it runs the other users cannot sign in;
 * the owner stays signed in and sees a blocking progress modal until the account is empty and
 * onboarding starts again.
 */
@Injectable({
  providedIn: 'root',
})
export class AccountResetService {
  private readonly api = inject(AccountResetApi);
  private readonly profileService = inject(ProfileService);
  private readonly dialog = inject(MatDialog);

  /**
   * Requests the reset, or resumes a failed one, and blocks the app with the progress modal until
   * it has finished. Only the account owner may.
   */
  requestReset(): Observable<void> {
    return this.api.resetAccount().pipe(tap(() => this.openProgress()));
  }

  /** Blocks the app with the progress modal until the reset has finished. */
  openProgress(): void {
    if (this.dialog.getDialogById(AccountResetProgressModalComponent.dialogId)) {
      return;
    }

    this.dialog.open(AccountResetProgressModalComponent, {
      id: AccountResetProgressModalComponent.dialogId,
      disableClose: true,
      width: '458px',
    });
  }

  /**
   * Shows the progress modal when the owner's account is being reset, e.g. after a reload, in
   * another tab, or for a reset requested through the Admin Portal.
   */
  openProgressIfRunning(): void {
    if (!this.profileService.isOwner()) {
      return;
    }

    this.api
      .getProgress()
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe(progress => {
        if (isResetRunning(progress)) {
          this.openProgress();
        }
      });
  }
}
